import type { RecordingId } from "@repo/app-types/entities/recording";
import type { AccountGradingRecording } from "@repo/app-types/entities/teammatePerformanceSummary";
import type { QuoteParagraph } from "@repo/app-types/helpers/gradeValidationHelpers";
import memoize from "lodash-es/memoize.js";

//
// Match quotes to transcript fragments.
//

const MINIMUM_WORDS_FOR_ORDERING = 5;
const FUZZY_MATCH_THRESHOLD = 0.8;
const FUZZY_MATCH_DISTANCE_MULTIPLIER = 1.5;

interface FuzzyMatch {
  start: number;
  end: number;
  score: number;
}

interface QuoteFullTextMatch {
  quoteId: string;
  start: number;
  end: number;
}

export interface QuoteRecordingMatch {
  quoteId: string;
  quoteIndex: number;
  recordingId: RecordingId;
  recordingFragmentIndex: number;
  inFragmentIndex: number;
  index: number;
  length: number;
}

type QuoteToMatchesMap = Map<string, QuoteRecordingMatch[]>;
type RecordingToMatchesMap = Map<
  RecordingId,
  Map<number, QuoteRecordingMatch[]>
>;

// Data structure to cache matches for quick access.
export interface QuoteMatchMap {
  quoteToMatchesMap: QuoteToMatchesMap;
  recordingToMatchesMap: RecordingToMatchesMap;
}

function findByQuote(
  map: QuoteMatchMap,
  quoteId: string,
): readonly QuoteRecordingMatch[] | undefined {
  return map.quoteToMatchesMap.get(quoteId);
}

function findByRecording(
  map: QuoteMatchMap,
  recordingId: RecordingId,
  fragmentIndex: number,
): readonly QuoteRecordingMatch[] | undefined {
  return map.recordingToMatchesMap.get(recordingId)?.get(fragmentIndex);
}

/**
 * Build a `QuoteMatchMap` from a flat list of matches — the same indexing
 * `findMatches` uses internally, exposed so callers that construct their own
 * `QuoteRecordingMatch`es (e.g. transcript search) can reuse the render path.
 */
function fromMatches(matches: readonly QuoteRecordingMatch[]): QuoteMatchMap {
  const quoteToMatchesMap: QuoteToMatchesMap = new Map();
  const recordingToMatchesMap: RecordingToMatchesMap = new Map();

  for (const match of matches) {
    let quoteMatches = quoteToMatchesMap.get(match.quoteId);
    if (!quoteMatches) {
      quoteMatches = [];
      quoteToMatchesMap.set(match.quoteId, quoteMatches);
    }
    quoteMatches.push(match);

    let recordingFragments = recordingToMatchesMap.get(match.recordingId);
    if (!recordingFragments) {
      recordingFragments = new Map();
      recordingToMatchesMap.set(match.recordingId, recordingFragments);
    }
    let fragmentMatches = recordingFragments.get(match.recordingFragmentIndex);
    if (!fragmentMatches) {
      fragmentMatches = [];
      recordingFragments.set(match.recordingFragmentIndex, fragmentMatches);
    }
    fragmentMatches.push(match);
  }

  return { quoteToMatchesMap, recordingToMatchesMap };
}

export const QuoteMatchMap = {
  findByQuote,
  findByRecording,
  fromMatches,
};

function newQuoteMatcher(recordings: readonly AccountGradingRecording[]) {
  // Combine everything into one string.
  const recordingsText = recordings
    .map(({ transcript }) => {
      if (!transcript) {
        return "";
      }

      const { fragments } = transcript;
      return fragments.map((f) => f.text_transcript).join(" ");
    })
    .join(" ")
    .toLowerCase();

  // Find a match in the combined string.
  const findFullTextMatchForQuote = (
    quote: QuoteParagraph,
    previousQuoteLastMatch: QuoteRecordingMatch | undefined,
  ): QuoteFullTextMatch | undefined => {
    const lowerQuote = quote.long.toLowerCase();
    const words = lowerQuote.split(" ");

    // For short quotes, make sure it's after the previous when possible.
    let startIndex = 0;
    if (words.length < MINIMUM_WORDS_FOR_ORDERING && previousQuoteLastMatch) {
      startIndex = previousQuoteLastMatch.index + previousQuoteLastMatch.length;
    }

    // Check if we have an exact match.
    const exactMatchIndex = recordingsText.indexOf(lowerQuote, startIndex);
    if (exactMatchIndex >= 0) {
      return {
        quoteId: quote.id,
        start: exactMatchIndex,
        end: exactMatchIndex + lowerQuote.length,
      };
    }

    // If not, iterate to find the best match.
    let bestMatch: FuzzyMatch = {
      start: -1,
      end: -1,
      score: 0,
    };

    for (let i = startIndex; i < recordingsText.length; i++) {
      let matchedWords = 0;
      let firstWordIndex = -1;
      let lastWordIndex = i;

      for (const word of words) {
        const wordIndex = recordingsText.indexOf(word, lastWordIndex);
        if (wordIndex < 0) {
          continue;
        }

        if (firstWordIndex < 0) {
          firstWordIndex = wordIndex;
          i = firstWordIndex;
        }

        if (
          wordIndex - firstWordIndex <
          lowerQuote.length * FUZZY_MATCH_DISTANCE_MULTIPLIER
        ) {
          matchedWords++;
          lastWordIndex = wordIndex + word.length;
        }
      }

      const score = matchedWords / words.length;
      if (score > bestMatch.score && score > FUZZY_MATCH_THRESHOLD) {
        bestMatch = {
          start: firstWordIndex,
          end: lastWordIndex,
          score,
        };
      }
    }

    if (bestMatch.start < 0) {
      return undefined;
    }

    return {
      quoteId: quote.id,
      start: bestMatch.start,
      end: bestMatch.end,
    };
  };

  // Find matches across recordings and fragments.
  const findMatchesForQuote = (
    quote: QuoteParagraph,
    previousQuoteLastMatch: QuoteRecordingMatch | undefined,
  ) => {
    // 1. Find a match in the combined string.
    const fullTextMatch = findFullTextMatchForQuote(
      quote,
      previousQuoteLastMatch,
    );
    if (!fullTextMatch) {
      return [];
    }

    // 2. Map the full text match to recording/fragment matches.
    const matches: QuoteRecordingMatch[] = [];
    let index = 0;
    let quoteIndex = 0;

    for (const recording of recordings) {
      if (index > fullTextMatch.end) {
        break;
      }

      const { transcript } = recording;
      if (!transcript) {
        index++;
        continue;
      }

      for (const entry of transcript.fragments.entries()) {
        if (index > fullTextMatch.end) {
          break;
        }

        const [fragmentIndex, fragment] = entry;
        const fragmentLength = fragment.text_transcript.length;
        const endIndex = index + fragmentLength;

        // Contained match.
        if (fullTextMatch.start >= index && fullTextMatch.end <= endIndex) {
          matches.push({
            quoteId: quote.id,
            quoteIndex: quoteIndex++,
            recordingId: recording.id,
            recordingFragmentIndex: fragmentIndex,
            inFragmentIndex: fullTextMatch.start - index,
            index: fullTextMatch.start,
            length: fullTextMatch.end - fullTextMatch.start,
          });
        }
        // Start match.
        else if (
          fullTextMatch.start >= index &&
          fullTextMatch.start < endIndex
        ) {
          matches.push({
            quoteId: quote.id,
            quoteIndex: quoteIndex++,
            recordingId: recording.id,
            recordingFragmentIndex: fragmentIndex,
            inFragmentIndex: fullTextMatch.start - index,
            index: fullTextMatch.start,
            length: endIndex - fullTextMatch.start,
          });
        }
        // End match.
        else if (fullTextMatch.end > index && fullTextMatch.end <= endIndex) {
          matches.push({
            quoteId: quote.id,
            quoteIndex: quoteIndex++,
            recordingId: recording.id,
            recordingFragmentIndex: fragmentIndex,
            inFragmentIndex: 0,
            index,
            length: fullTextMatch.end - index,
          });
        }
        // Middle match — the fragment lies fully inside the quote span.
        else if (fullTextMatch.start < index && fullTextMatch.end > endIndex) {
          matches.push({
            quoteId: quote.id,
            quoteIndex: quoteIndex++,
            recordingId: recording.id,
            recordingFragmentIndex: fragmentIndex,
            inFragmentIndex: 0,
            index,
            length: fragmentLength,
          });
        }

        index += fragmentLength + 1;
      }
    }

    return matches;
  };

  const memoizedFindMatchesForQuote = memoize(findMatchesForQuote);

  // Find all matches for the provided quotes and return a quick access map.
  const findMatches = (quotes: readonly QuoteParagraph[]): QuoteMatchMap => {
    // 1. Find all matches.
    const allMatches = quotes.reduce((result: QuoteRecordingMatch[], quote) => {
      const previousQuoteLastMatch = result.at(-1);
      const quoteMatches = memoizedFindMatchesForQuote(
        quote,
        previousQuoteLastMatch,
      );
      result.push(...quoteMatches);
      return result;
    }, []);

    // 2. Build the QuoteMatchMap for quick access in the UI.
    return fromMatches(allMatches);
  };

  return { findMatches };
}

export const QuoteMatcher = {
  new: newQuoteMatcher,
};
